import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Input, Label } from '../../design-system';
import { resolveTenant } from '../../services/consentApi';
import { useI18n } from '../../i18n';
import '../../design-system/styles.css';

/**
 * General Portal Entry Page - Find your organization
 * Route: /my-consent
 * 
 * Users can enter their organization name/code to be redirected
 * to the tenant-specific portal login.
 */
const PortalTenantSelector: React.FC = () => {
  const navigate = useNavigate();
  const { t, language, setLanguage, supportedLanguages } = useI18n();

  const [tenantInput, setTenantInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    const slug = tenantInput.trim().toLowerCase().replace(/\s+/g, '-');
    
    if (!slug) {
      setError(t.portal?.enterOrgName || 'Please enter your organization name or code');
      return;
    }
    
    setLoading(true);
    
    try {
      // Verify tenant exists
      await resolveTenant(slug);
      // Redirect to tenant-specific portal
      navigate(`/t/${slug}/my-consent/login`);
    } catch (err) {
      setError(t.portal?.orgNotFound || 'Organization not found. Please check the name and try again.');
    } finally {
      setLoading(false);
    }
  }, [tenantInput, navigate, t]);

  return (
    <main style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, var(--ds-bg-secondary) 0%, white 100%)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Language selector in header */}
      <div style={{ 
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'flex-end',
      }}>
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value as any)}
          style={{
            padding: '8px 12px',
            borderRadius: 8,
            border: '1px solid var(--ds-border)',
            fontSize: 14,
            background: 'white',
          }}
        >
          {supportedLanguages.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.nativeName}
            </option>
          ))}
        </select>
      </div>

      <div style={{ 
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 16px',
      }}>
        <div style={{ maxWidth: 480, width: '100%' }}>
          <Card>
            {/* Header */}
            <header style={{ textAlign: 'center', marginBottom: 32 }}>
              <div style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'var(--ds-accent-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                fontSize: 36,
              }}>
                🔐
              </div>
              <h1 style={{ fontSize: 28, margin: '0 0 8px' }}>
                {t.portal?.myConsents || 'My Consents'}
              </h1>
              <p style={{ 
                margin: 0, 
                color: 'var(--ds-text-secondary)',
                fontSize: 16,
                lineHeight: 1.5,
              }}>
                {t.portal?.viewManageConsents || 'View and manage your consent artifacts'}
              </p>
            </header>

            {/* Error message */}
            {error && (
              <div className="ds-alert ds-alert--error" role="alert" style={{ marginBottom: 20 }}>
                {error}
              </div>
            )}

            {/* Organization finder form */}
            <form onSubmit={handleSubmit} noValidate>
              <div className="ds-field" style={{ marginBottom: 20 }}>
                <Label htmlFor="tenant" style={{ fontSize: 16, marginBottom: 8 }}>
                  {t.portal?.organizationNameOrCode || 'Organization Name or Code'}
                </Label>
                <Input
                  type="text"
                  id="tenant"
                  value={tenantInput}
                  onChange={(e) => setTenantInput(e.target.value)}
                  placeholder={t.portal?.orgPlaceholder || 'e.g., globalhealth or acme-corp'}
                  style={{ 
                    fontSize: 18, 
                    padding: '16px',
                    height: 56,
                  }}
                  autoFocus
                />
                <p className="ds-text-muted" style={{ fontSize: 14, marginTop: 8 }}>
                  {t.portal?.orgHint || 'Enter the name or code provided by your organization'}
                </p>
              </div>

              <Button 
                type="submit" 
                variant="primary" 
                isLoading={loading} 
                disabled={loading}
                style={{ 
                  width: '100%', 
                  padding: '16px',
                  fontSize: 18,
                  height: 56,
                }}
              >
                {loading ? t.common.loading : (t.portal?.findMyOrganization || 'Find My Organization')}
              </Button>
            </form>

            {/* Divider */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 12,
              margin: '24px 0',
            }}>
              <div style={{ flex: 1, height: 1, background: 'var(--ds-border)' }} />
              <span style={{ fontSize: 14, color: 'var(--ds-text-muted)' }}>
                {t.portal?.or || 'or'}
              </span>
              <div style={{ flex: 1, height: 1, background: 'var(--ds-border)' }} />
            </div>

            {/* Direct URL hint */}
            <div style={{ 
              background: 'var(--ds-bg-secondary)',
              padding: 16,
              borderRadius: 8,
              textAlign: 'center',
            }}>
              <p style={{ margin: '0 0 8px', fontSize: 14, color: 'var(--ds-text-secondary)' }}>
                {t.portal?.haveDirectLink || 'Have a direct link from your organization?'}
              </p>
              <p style={{ margin: 0, fontSize: 14, color: 'var(--ds-text-muted)' }}>
                {t.portal?.useDirectLink || 'Use the link they provided (e.g., yoursite.com/t/orgname/my-consent/login)'}
              </p>
            </div>
          </Card>

          {/* Privacy info */}
          <div style={{ 
            textAlign: 'center', 
            marginTop: 24,
            padding: '0 16px',
          }}>
            <p style={{ fontSize: 14, color: 'var(--ds-text-muted)', margin: '0 0 8px' }}>
              {t.portal?.privacyProtected || 'Your privacy is protected by law'}
            </p>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: 16,
              flexWrap: 'wrap',
              fontSize: 14,
              color: 'var(--ds-text-muted)',
            }}>
              <span>🇮🇳 DPDP Act</span>
              <span>🇪🇺 GDPR</span>
              <span>🇺🇸 CCPA</span>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default PortalTenantSelector;
